import { Link } from 'react-router-dom';
import { Section, SectionHeading } from '../layout/Section.jsx';
import { imageProps, formatDate, plainText } from '../../lib/format.js';

/**
 * The three most recent posts.
 *
 * Square-cornered images on a cream ground, a gold category line, the title and
 * a short excerpt — the same card shape as the product shelves above it, so the
 * page ends in the rhythm it started in.
 */
export const HomeJournal = ({ posts }) => {
  if (!posts?.length) return null;

  return (
    <Section className="border-t border-mist">
      <SectionHeading eyebrow="Reading" title="From the blog" action={{ label: 'All writing', to: '/blogs' }} />
      <div className="grid gap-x-4 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {posts.slice(0, 3).map((post) => (
          <article key={post._id} className="flex flex-col border border-mist bg-surface">
            <Link
              to={`/blogs/${post.slug}`}
              className="group flex h-full flex-col focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              <div className="aspect-3/2 overflow-hidden bg-cream">
                {post.featuredImage && (
                  <img
                    {...imageProps(
                      { url: post.featuredImage, alt: post.featuredImageAlt || post.title },
                      { sizes: '(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw' },
                    )}
                    className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <p className="text-2xs font-medium tracking-[0.12em] text-brand uppercase">
                  {[post.categories?.[0]?.name, post.readTimeMinutes ? `${post.readTimeMinutes} min read` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <h3 className="mt-2 text-base leading-snug text-ink group-hover:text-brand">{post.title}</h3>
                {post.excerpt && (
                  <p className="mt-2 text-xs leading-relaxed text-muted">{plainText(post.excerpt, 130)}</p>
                )}
                <p className="mt-auto flex items-center justify-between pt-4 text-2xs text-muted">
                  <span>{formatDate(post.publishedAt)}</span>
                  <span className="font-semibold tracking-[0.1em] text-brand uppercase">
                    Read more
                    <span aria-hidden="true" className="ml-1">
                      →
                    </span>
                  </span>
                </p>
              </div>
            </Link>
          </article>
        ))}
      </div>
    </Section>
  );
};

export default HomeJournal;
